import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Icon,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { useFeatureFlags } from '@domain';
import { useMerchantGroup, useMerchantTheme } from '@domain/merchants/queries';
import { SpotOnO } from '@olo-web/assets/icons/SpotOnO.ui';
import { useCustomThemeDraftState } from '@olo-web/client-state';
import { MotionBox } from '@olo-web/components/atoms/motion';
import { useThemeRoles } from '@olo-web/domain/firebaseAuth';
import { ECustomThemeViews } from '@olo-web/types/enums';
import { useThemeAuthUser } from '@olo-web/utils/google/firebase';
import isObject from 'lodash/isObject';
import React, { useMemo } from 'react';
import { FirebaseAuth } from '..';
import { AccessRestricted } from './AccessRestricted';
import { ColorPalette } from './ColorPalette';
import { CreateTheme } from './CreateTheme';
import { MerchantPixelsAndKeys } from './MerchantPixelsAndKeys';
import { MerchantSettings } from './MerchantSettings';
import { PayAtTableQRCodes } from './PayAtTableQRCodes';
import { RevertToDefaultButton } from './RevertToDefaultButton';
import { ThemeFonts } from './ThemeFonts';
import { ThemeImage } from './ThemeImage';
import { ThemeMenu } from './ThemeMenu';
import { ThemeMerchantLocations } from './ThemeMerchantLocations';
import { ThemeUsers } from './ThemeUsers';

const CustomThemeDrawer = () => {
  const { data: group, isLoading: groupLoading } = useMerchantGroup();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data: theme, isLoading: themeLoading } = useMerchantTheme();
  const user = useThemeAuthUser();
  const { view } = useCustomThemeDraftState();
  const { isAdmin, isOwner, isEditor } = useThemeRoles();
  const { data: flags } = useFeatureFlags();

  const merchantIds = useMemo(() => {
    if (groupLoading || !group?.merchants?.length) return null;
    return group.merchants.map((m) => m.merchantId);
  }, [groupLoading, group?.merchants]);

  const canCreateTheme = useMemo(() => {
    if (themeLoading || !merchantIds?.length || !!theme?.id) return false;
    return true;
  }, [themeLoading, merchantIds, theme?.id]);

  const canEditTheme = useMemo(() => {
    return isAdmin || isOwner || isEditor;
  }, [isAdmin, isOwner, isEditor]);

  const Component = useMemo(() => {
    if (!canEditTheme) return AccessRestricted;
    if (canCreateTheme) return CreateTheme;
    switch (view) {
      case ECustomThemeViews.COLOR:
        return ColorPalette;
      case ECustomThemeViews.FONTS:
        return ThemeFonts;
      case ECustomThemeViews.LOGO:
      case ECustomThemeViews.BACKGROUND_IMAGE:
      case ECustomThemeViews.FAVICON:
        return ThemeImage;
      case ECustomThemeViews.ADMIN:
      case ECustomThemeViews.MANAGE_ACCESS:
        return ThemeUsers;
      case ECustomThemeViews.MANAGE_LOCATIONS:
        return ThemeMerchantLocations;
      case ECustomThemeViews.PIXELS_AND_KEYS:
        return MerchantPixelsAndKeys;
      case ECustomThemeViews.SETTINGS:
        return MerchantSettings;
      case ECustomThemeViews.QR_CODES:
        return flags?.enableQRCodePrinting?.on ? PayAtTableQRCodes : null;
      default:
        return null;
    }
  }, [view, canEditTheme, canCreateTheme, flags?.enableQRCodePrinting]);

  const showRevertButton = useMemo(() => {
    if (!Component || !canEditTheme) return false;
    switch (view) {
      case ECustomThemeViews.LOGO:
      case ECustomThemeViews.BACKGROUND_IMAGE:
      case ECustomThemeViews.FAVICON:
        return !!theme?.[view];
      case ECustomThemeViews.FONTS:
      case ECustomThemeViews.COLOR:
        return isObject(theme?.[view]) && !!Object.keys(theme?.[view] || {})?.length;
      default:
        return false;
    }
  }, [view, theme, Component, canEditTheme]);

  const isAccessRelated = useMemo(() => {
    switch (view) {
      case ECustomThemeViews.LOGO:
      case ECustomThemeViews.COLOR:
      case ECustomThemeViews.FONTS:
      case ECustomThemeViews.BACKGROUND_IMAGE:
      case ECustomThemeViews.FAVICON:
        return false;
      default:
        return true;
    }
  }, [view]);

  return (
    <>
      <MotionBox
        pos="fixed"
        top="300px"
        zIndex={99}
        initial={false}
        animate={{
          x: '-122px',
        }}
        whileHover={{
          x: '0px',
          transition: {
            duration: 0.5,
          },
        }}
      >
        <Button
          colorScheme="informative"
          onClick={onOpen}
          borderRadius="0px 8px 8px 0"
          fontSize="1.25rem"
          px={3}
          py={5}
          fontFamily="Poppins"
          rightIcon={<Icon as={SpotOnO} />}
        >
          Edit theme
        </Button>
      </MotionBox>
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="md" preserveScrollBarGap>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            {canCreateTheme ? (
              <Text fontSize="lg">Create a theme</Text>
            ) : !user ? (
              <Text fontSize="lg">Sign in or sign up to customize your theme</Text>
            ) : (
              <Flex align="center" wrap="wrap">
                {!isAccessRelated && <Text fontSize="lg">Customize your</Text>}
                <ThemeMenu />
              </Flex>
            )}
          </DrawerHeader>
          <DrawerBody>
            {!user ? <FirebaseAuth /> : Component ? <Component onClose={onClose} /> : null}
          </DrawerBody>
          {showRevertButton && (
            <DrawerFooter>
              <RevertToDefaultButton />
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default CustomThemeDrawer;
